import { animate, style, transition, trigger } from '@angular/animations';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { PressEscapeKeyDirectiveModule } from '@commons/directives/press-escape-key/press-escape-key.directive';
import { DropdownModule } from '@commons/dropdown/dropdown.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { CastToSpecificFieldTypePipe } from '@domains/search-engine/cast-to-specific-field-type/cast-to-specific-field-type.pipe';
import { DropdownPanelModule } from '@domains/search-engine/dropdown-panel.directive';
import { SearchEngineFieldDropdown } from '@domains/search-engine/entities/dropdown-search-field';
import {
  SearchEngineField,
  SearchEngineFields,
  SearchEngineFieldType,
} from '@domains/search-engine/search-engine-field';
import { SearchEngineAddressSearchModule } from '@domains/search-engine/search-engine-fields/address-search/address-search.component';
import { SearchEngineCheckboxSearchModule } from '@domains/search-engine/search-engine-fields/checkbox-search/checkbox-search.component';
import { SearchEngineDateInputSearchModule } from '@domains/search-engine/search-engine-fields/date-input-search/date-input-search.component';
import { SearchEngineJobsEuresInputSearchModule } from '@domains/search-engine/search-engine-fields/jobs-eures-input-search/jobs-eures-input-search.component';
import { SearchEngineLocationEuresModule } from '@domains/search-engine/search-engine-fields/loaction-search-eures/location-search-eures.component';
import { SearchEngineLocationSearchPoleEmploiModule } from '@domains/search-engine/search-engine-fields/location-search-pole-emploi/location-search-pole-emploi.component';
import { SearchEngineLocationSearchModule } from '@domains/search-engine/search-engine-fields/location-search/location-search.component';
import { SearchEngineRomesInputSearchModule } from '@domains/search-engine/search-engine-fields/romes-input-search/romes-input-search.component';
import { SelectDurationModule } from '@domains/search-engine/search-engine-fields/select-duration/select-duration.component';
import { SearchEngineSelectSearchModule } from '@domains/search-engine/search-engine-fields/select-search/select-search.component';
import { SelectWeeklyDurationModule } from '@domains/search-engine/search-engine-fields/select-weekly-duration/select-weekly-duration.component';
import { SearchEngineTextInputSearchModule } from '@domains/search-engine/search-engine-fields/text-input-search/text-input-search.component';
import { SearchEngineTextRomesInputSearchModule } from '@domains/search-engine/search-engine-fields/text-romes-input-search/text-romes-input-search.component';
import { PoleEmploiContract } from '@models/pole-emploi/contract';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { trackEventWithAtInternet as trackEvent } from '@wizbii/tracking';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith, take, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-search-engine',
  templateUrl: './search-engine.component.html',
  styleUrls: [
    './search-engine.component.scss',
    './search-engine-mobile-required.scss',
    './search-engine-filters-modal.scss',
    './advanced-search-container.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('advancedFiltersOnEnterLeave', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-100%)' }),
        animate('400ms cubic-bezier(.77,.95,.79,.99)'),
      ]),

      transition(':leave', [
        animate('500ms cubic-bezier(.21,.01,.23,.05)'),
        style({ opacity: 0, transform: 'translateY(-100%)' }),
      ]),
    ]),
  ],
})
export class SearchEngineComponent implements OnInit, OnDestroy {
  private static Counter = 0;
  private readonly _id = `advanced-search-engine-${++SearchEngineComponent.Counter}`;

  @Input() fields!: SearchEngineFields;
  @Input() displayAsOpenedModal = false;

  searchForm?: FormGroup;
  formSubmited$ = new BehaviorSubject<boolean>(false);

  advancedFiltersIsOpen$ = new BehaviorSubject<boolean>(false);
  canAdvancedFiltersClose$ = new BehaviorSubject<boolean>(true);
  nbAdvancedFilters$ = new BehaviorSubject<number | undefined>(0);
  modalOpen$ = new BehaviorSubject<boolean>(false);
  displayAsOpenedModal$!: Observable<boolean>;
  formErrors$!: Observable<string[]>;

  isInitialized = false;

  SearchEngineFieldType = SearchEngineFieldType;

  @ViewChild('filtersMobile') dialogRef?: TemplateRef<any>;
  filtersModal?: MatDialogRef<any>;

  get isMobileOrTablet$(): Observable<boolean> {
    return this.breakpointsService.isMobileOrTablet$;
  }

  get elm_id(): string {
    return this._id;
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly breakpointsService: BreakpointsService,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.displayAsOpenedModal$ = combineLatest([this.modalOpen$, this.isMobileOrTablet$]).pipe(
      untilDestroyed(this),
      map(([modalOpen, isMobileOrTablet]) => {
        return modalOpen || (isMobileOrTablet && this.displayAsOpenedModal);
      })
    );

    this.searchForm = this.formBuilder.group({
      [this.fields.mainField.name]: [undefined, this.fields.mainField.required ? [Validators.required] : []],
    });

    this.fields.submainFields.concat(this.fields?.advancedFields || []).forEach((field) => {
      if (!this.searchForm) {
        return;
      }
      this.searchForm.addControl(field.name, new FormControl(undefined, field.required ? [Validators.required] : []));
    });

    this.formErrors$ = this.getFormErrors();

    this.route.queryParamMap
      .pipe(
        tap(() => (this.isInitialized = false)),
        untilDestroyed(this),
        map((q) => {
          [this.fields.mainField]
            .concat(this.fields?.advancedFields || [], this.fields.submainFields)
            .forEach((field) => {
              const value = field.paramMapToValue(q);
              if (value) {
                this.searchForm?.get(field.name)?.setValue(value);
              }
            });
        })
      )
      .subscribe({
        next: () => {
          this.isInitialized = true;
        },
      });
  }

  private getFormErrors(): Observable<string[]> {
    return combineLatest([this.formSubmited$, this.searchForm?.valueChanges.pipe(startWith({}))]).pipe(
      map(([formSubmited]) => {
        if (!formSubmited && (!this.searchForm?.touched || (this.searchForm?.touched && !this.searchForm?.invalid))) {
          return [];
        }

        const allFields = [
          this.fields.mainField,
          ...(this.fields?.submainFields || []),
          ...(this.fields?.advancedFields || []),
        ];
        const requiredFields = allFields.filter((field) => field.required);
        const invalidFields = requiredFields.filter(
          (field) =>
            (formSubmited || this.searchForm?.get(field.name)?.touched) && this.searchForm?.get(field.name)?.invalid
        );
        if (invalidFields.length && allFields.length === requiredFields.length) {
          return ['Tous les champs sont requis'];
        }

        return invalidFields.map((field) => `Le champ ${field.placeholder} est requis`);
      })
    );
  }

  onSubmit(): void {
    this.formSubmited$.next(true);

    if (this.searchForm?.invalid) {
      return;
    }

    if (this.isInitialized) {
      const queryParams: Record<string, any> = { ...this.fields.staticQueryParams, ...this.queryParamsFromFileds() };

      this.router.navigate(['/', this.fields.redirectUri], {
        queryParams: { ...queryParams },
        preserveFragment: true,
      });
    }
  }

  private queryParamsFromFileds() {
    return this.fields.submainFields
      .concat(this.fields?.advancedFields || [], [this.fields.mainField])
      .reduce((queryParams: Record<string, any>, field) => {
        if (field) {
          queryParams = { ...queryParams, ...field.valueToQueryParams(this.searchForm?.get(field.name)?.value) };
        }

        return queryParams;
      }, {});
  }

  toggleCanAdvancedFiltersClose(isPanelOpen: boolean): void {
    this.canAdvancedFiltersClose$.next(!isPanelOpen);
  }

  closeAdvancedFilters(): void {
    if (this.canAdvancedFiltersClose$.value) {
      this.advancedFiltersIsOpen$.next(false);
    }
  }

  openFiltersModal(): void {
    if (!this.dialogRef) {
      return;
    }

    this.modalOpen$.next(true);
    this.filtersModal = this.dialog.open(this.dialogRef, {
      panelClass: 'filters-modal',
      autoFocus: false,
      minWidth: '100vw',
    });
  }

  closeFiltersModal(): void {
    if (!this.filtersModal) {
      return;
    }

    trackEvent({
      category: this.fields.trackEventName,
      action: 'Click Rechercher',
      type: 'action',
    });

    this.filtersModal.close();
  }

  trackFilterAndSearchButton(): void {
    this.isMobileOrTablet$.pipe(take(1)).subscribe({
      next: (isMobileOrTablet) => {
        trackEvent({
          category: this.fields.trackEventName,
          action: isMobileOrTablet ? 'Click Filtres' : 'Click Rechercher',
          type: 'action',
        });
      },
    });
  }

  clickAdvancedFilters(): void {
    if (!this.advancedFiltersIsOpen$.value) {
      trackEvent({ category: this.fields.trackEventName, action: 'Click Filtres', type: 'action' });
    }
    this.advancedFiltersIsOpen$.next(!this.advancedFiltersIsOpen$.value);
  }

  fieldHasChanged(liveSearch: boolean): void {
    this.nbAdvancedFilters$.next(
      this.fields?.advancedFields
        ?.map((field) => this.searchForm?.get(field.name)?.value)
        .filter(
          (value) =>
            (!!value && (typeof value === 'object' || value?.length > 0)) || (typeof value === 'boolean' && value)
        ).length
    );

    if (liveSearch) {
      this.onSubmit();
    }
  }

  updateDropdownPanelState(field: SearchEngineField, status: boolean): void {
    (field as SearchEngineFieldDropdown).panelIsOpen = status;
  }

  trackByContracts(_: number, contract: PoleEmploiContract): string {
    return contract.code;
  }

  trackBySearchEngineField(_: number, searchEngineField: SearchEngineField): string {
    return searchEngineField.name;
  }

  trackByString(index: number, str: string): string {
    return `${str}-${index}`;
  }

  ngOnDestroy(): void {
    this.advancedFiltersIsOpen$.complete();
    this.nbAdvancedFilters$.complete();
    this.modalOpen$.complete();
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDialogModule,
    DropdownPanelModule,
    DropdownModule,
    SearchEngineSelectSearchModule,
    SearchEngineLocationSearchModule,
    SearchEngineLocationEuresModule,
    SearchEngineAddressSearchModule,
    SearchEngineLocationSearchPoleEmploiModule,
    SearchEngineTextInputSearchModule,
    SearchEngineDateInputSearchModule,
    SearchEngineCheckboxSearchModule,
    PressEscapeKeyDirectiveModule,
    A11yModule,
    SelectDurationModule,
    SearchEngineRomesInputSearchModule,
    SearchEngineTextRomesInputSearchModule,
    SearchEngineJobsEuresInputSearchModule,
    SelectWeeklyDurationModule,
  ],
  declarations: [SearchEngineComponent, CastToSpecificFieldTypePipe],
  exports: [SearchEngineComponent],
})
export class SearchEngineModule {}
